import { ArrowBack, CheckCircleTwoTone, ExpandMore, KeyboardArrowLeft, KeyboardArrowRight } from '@mui/icons-material'
import { Accordion, AccordionDetails, AccordionSummary, Button, Card, CardContent, Divider, Grid, Step, StepLabel, Stepper, Typography } from '@mui/material'
import { GreenText, LabelField, PhotoCard } from 'components'
import { useLayout } from 'context'
import { getOutboundTripDetail, uploadDriversLicensePhoto } from 'controllers/toDoLists/outboundTrips/index'
import { NeedsDriversLicensePhotoUploadedOutboundTripDetailDto } from 'dtos/toDoLists/outboundTrips/NeedsDriversLicensePhotoUploadedOutboundTripDetailDto'
import { enqueueSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { formatDateMoment } from 'utils'
import DriversLicensePhotoCaptureDialog from './DriversLicensePhotoCaptureDialog'

export default function NeedsDriversLicensePhotoUploaded() {
  const { setIsLayoutLoading } = useLayout()
  const { outboundTripGuid } = useParams()
  const navigate = useNavigate()

  // 0 = Photo Capture, 1 = Photo Review
  const [step, setStep] = useState<number>(0)
  const [outboundTripDetail, setOutboundTripDetail] = useState<NeedsDriversLicensePhotoUploadedOutboundTripDetailDto | undefined>(undefined)

  // Step 0 - Photo Capture State
  const [dialogOpen, setDialogOpen] = useState<boolean>(false)
  const [objectURL, setObjectURL] = useState<string | undefined>(undefined)

  // Step 1 - Photo Review State
  // None

  useEffect(() => {
    if (!outboundTripGuid) return

    setIsLayoutLoading(true)
    getOutboundTripDetail(outboundTripGuid)
      .then(res => setOutboundTripDetail(res.value))
      .finally(() => setIsLayoutLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outboundTripGuid])

  return (
    <Grid container spacing={2} alignItems='center'>
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Grid container alignItems='center' justifyContent='space-between'>
              <Grid item>
                <Typography fontWeight='bold' variant='h6'>
                  Needs Drivers License Uploaded
                </Typography>
              </Grid>

              <Grid item>
                <Button color='primary' onClick={() => navigate(-1)} startIcon={<ArrowBack />} variant='text'>
                  BACK
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12}>
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMore />} aria-controls='panel1-content' id='panel1-header'>
            <Typography fontWeight='bold' variant='h6'>
              Outbound Trip
            </Typography>
          </AccordionSummary>

          <AccordionDetails>
            <Grid container spacing={1} alignItems='center'>
              <Grid item xs={12}>
                <Divider />
              </Grid>

              <Grid item xs={12}>
                <LabelField label='Outbound Trip #' value={outboundTripDetail?.outboundTripNumber} />
              </Grid>

              <Grid item xs={12}>
                <LabelField label='Outbound Trip Date' value={formatDateMoment(outboundTripDetail?.outboundTripDate)} />
              </Grid>

              <Grid item xs={12}>
                <LabelField label='Outbound Transporter Company' value={outboundTripDetail?.outboundTransporterCompany} />
              </Grid>

              <Grid item xs={12}>
                <LabelField label='Vehicles In Outbound Trip' value={outboundTripDetail?.vehiclesInOutboundTrip} />
              </Grid>

              <Grid item xs={12}>
                <LabelField label='Internal Notes' value={outboundTripDetail?.internalNotes} />
              </Grid>

              <Grid item xs={12}>
                <GreenText variant='outlined'>
                  {`**Outbound Trip Information Card Accordion:**

Clicking the Outbound Trip Information card will expanding the accordion displaying additional details about the Outbound Trip

**Outbound Trip # Label:**

Displays the unique identifier of the Outbound Trip to which the vehicle is assigned.

**Outbound Trip Date Label:**

Displays the scheduled date of the Outbound Trip.

**Outbound Transporter Company Label:**

Displays the company name for the Outbound Transporter Company.

**Vehicles In Outbound Trip Label:**

Displays the total number of vehicles in the Outbound Trip.

**Internal Notes Label:**

Displays any entered notes internal notes for the Outbound Trip.`}
                </GreenText>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      </Grid>

      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Grid container spacing={2} alignItems='center' justifyContent='flex-start'>
              <Grid item xs={12}>
                <Stepper alternativeLabel activeStep={step}>
                  <Step>
                    <StepLabel>Photo Capture</StepLabel>
                  </Step>

                  <Step>
                    <StepLabel>Photo Review</StepLabel>
                  </Step>
                </Stepper>
              </Grid>

              <Grid item xs={12}>
                <Divider />
              </Grid>

              {step === 0 && (
                <>
                  <DriversLicensePhotoCaptureDialog
                    initialValues={outboundTripDetail!}
                    isLoading={false}
                    onClose={() => setDialogOpen(false)}
                    onSave={values => {
                      setOutboundTripDetail({ ...outboundTripDetail, ...values })
                      setDialogOpen(false)
                      if (objectURL) URL.revokeObjectURL(objectURL)
                      setObjectURL(URL.createObjectURL(values.driversLicensePhotoFile!))
                    }}
                    open={dialogOpen}
                  />

                  <Grid item xs={12} container alignItems='center' justifyContent='center'>
                    <Grid item>
                      <PhotoCard
                        label='Drivers License Photo'
                        onClick={() => setDialogOpen(true)}
                        src={objectURL}
                        complete={Boolean(outboundTripDetail?.driversLicensePhotoFile)}
                      />
                    </Grid>
                  </Grid>

                  <Grid item xs={12}>
                    <Button
                      disabled={!outboundTripDetail?.driversLicensePhotoFile}
                      fullWidth
                      startIcon={<KeyboardArrowRight />}
                      onClick={() => setStep(1)}
                      variant='contained'
                    >
                      NEXT
                    </Button>
                  </Grid>

                  <Grid item xs={12}>
                    <GreenText variant='outlined'>
                      {`**To Do List Steps:**

Displays the current step of the for the To Do List in the Needs Drivers License Uploaded Process.

**Driver's License Photo:**

Displays the Drivers Licenses Photo image uploaded by the user.

If no image has been uploaded, a “No Image” icon will be displayed instead.

Clicking the Drivers License Photo will open the Capture Drivers License Photo Photo Dialog.

**NEXT Button:**

Clicking this button will:

   - Save the uploaded photo to the database.
   - Advance the user to the next step in the process.

This button remains disabled until a photo has been uploaded.`}
                    </GreenText>
                  </Grid>
                </>
              )}

              {step === 1 && (
                <>
                  <Grid item xs={12} container spacing={1} alignItems='center' justifyContent='flex-start'>
                    <Grid item>
                      <CheckCircleTwoTone color='success' />
                    </Grid>

                    <Grid item>
                      <Typography variant='body1'>Drivers License Photo</Typography>
                    </Grid>
                  </Grid>

                  <Grid item xs={12}>
                    <Button
                      fullWidth
                      onClick={() => {
                        setIsLayoutLoading(true)
                        uploadDriversLicensePhoto(outboundTripDetail!)
                          .then(() => {
                            enqueueSnackbar('Drivers License Photo Uploaded Successfully!', { variant: 'success' })
                            navigate('/to-do-list', { replace: true })
                          })
                          .finally(() => setIsLayoutLoading(false))
                      }}
                      variant='contained'
                    >
                      SUBMIT
                    </Button>
                  </Grid>

                  <Grid item xs={12}>
                    <Button fullWidth startIcon={<KeyboardArrowLeft />} onClick={() => setStep(0)} variant='contained'>
                      PREVIOUS
                    </Button>
                  </Grid>

                  <Grid item xs={12}>
                    <GreenText variant='outlined'>
                      {`**To Do List Steps:**

Displays the current step of the for the To Do List in the Needs Drivers License Uploaded Process.

**Drivers License Photo Check Mark:**

Displays a check mark to indicate that the Drivers Licenses Photo has been successfully uploaded.

**SUBMIT Button:**

Clicking this button will:

Upload the Drivers License Photo  and associate it with the Outbound record, including:

   - The name of the user who completed the process.
   - A date/time stamp.

Return the user to the Needs Drivers License Uploaded to-do list screen.

**PREVIOUS Button:**

Clicking this button will:

   - Return the user to the previous step in the process.`}
                    </GreenText>
                  </Grid>
                </>
              )}
            </Grid>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  )
}
